import { AutocompleteInputChangeReason, ChipTypeMap } from "@mui/material";
import { FocusEvent, SyntheticEvent, useState } from "react";
import { Control, FieldPath, FieldValues } from "react-hook-form";
import {
  AutocompleteElement,
  AutocompleteElementProps,
  useController,
  useFormContext,
} from "react-hook-form-mui";

import { defaultGetOptionLabel, handleAutocompleteBlurMatch } from "./autocomplete-blur-utils";

export const AAutocompleteElement = <
  TValue,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: AutocompleteElementProps<
    TValue,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipTypeMap["defaultComponent"],
    TFieldValues,
    TName
  >
) => {
  const { name, control, options, multiple, autocompleteProps, ...rest } = props;
  const formContext = useFormContext<TFieldValues>();
  const formControl = (control ?? formContext?.control) as Control<TFieldValues>;
  const { field } = useController<TFieldValues, TName>({
    name,
    control: formControl,
  });

  const managesInputValue = Boolean(multiple);
  const [uncontrolledInputValue, setUncontrolledInputValue] = useState("");
  const inputValue = managesInputValue
    ? autocompleteProps?.inputValue ?? uncontrolledInputValue
    : autocompleteProps?.inputValue;
  const getOptionLabel = autocompleteProps?.getOptionLabel ?? defaultGetOptionLabel;

  const clearInputValue = (event: SyntheticEvent) => {
    if (autocompleteProps?.inputValue === undefined) {
      setUncontrolledInputValue("");
    }
    autocompleteProps?.onInputChange?.(event, "", "reset");
  };

  const handleInputChange = (
    event: SyntheticEvent,
    newInputValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (managesInputValue && autocompleteProps?.inputValue === undefined) {
      setUncontrolledInputValue(newInputValue);
    }
    autocompleteProps?.onInputChange?.(event, newInputValue, reason);
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const valueApplied = handleAutocompleteBlurMatch(
      event,
      {
        options,
        value: field.value,
        multiple,
        freeSolo: autocompleteProps?.freeSolo,
        getOptionLabel,
        isOptionEqualToValue: autocompleteProps?.isOptionEqualToValue,
      },
      (_event, newValue) => {
        field.onChange(newValue);
      }
    );

    if (valueApplied && multiple) {
      clearInputValue(event);
    }

    field.onBlur();
    autocompleteProps?.onBlur?.(event);
  };

  const mergedAutocompleteProps = {
    ...autocompleteProps,
    onBlur: handleBlur,
    ...(managesInputValue
      ? {
          inputValue,
          onInputChange: handleInputChange,
        }
      : {}),
  };

  return (
    <AutocompleteElement
      {...rest}
      name={name}
      control={formControl}
      options={options}
      multiple={multiple}
      autocompleteProps={mergedAutocompleteProps}
    />
  );
};
